import { useRef } from 'react';
import Tag from '@components/ui/Tag';
import {
  ProductTag,
  ProductTagFromHit,
  TagColour,
} from '@adapters/bigcommerce/types/product';
import { FEATURE_FLAG_PRODUCT_TAGS } from '@config/feature-flag';
import styles from './Tags.module.scss';
import { tagsFromFile } from '@adapters/bigcommerce/transforms/utils';

import { TagEventName, trackTagEvent } from '@utils/gtm/ga4/trackTagEvents';
import { determinePageType } from '@utils/gtm/ga4/utils';
import { isWithinDateRangePST } from '@utils/dateAndTime/isBetween';
import { usePdp } from '@context/PdpContext';

interface TagsContainerProps {
  tags: ProductTagFromHit[];
  clickHandler?: (arg: { path: string }) => void;
  id?: string;
  pdpPath?: string;
  activeVariantSku?: string;
  hitId?: string;
  locale?: string;
}

const RenderTagComponent = ({
  tag,
  clickHandler,
  pdpPath,
}: {
  tag: ProductTag;
  clickHandler?: (arg: { path: string }) => void;
  pdpPath?: string;
}) => {
  const ref = useRef<
    HTMLAnchorElement | HTMLButtonElement | HTMLSpanElement | null
  >(null);

  const clickWrapper =
    typeof clickHandler === 'function'
      ? (arg: Parameters<typeof clickHandler>[0]) => {
          trackTagEvent({
            eventName: TagEventName.selectTag,
            page: {
              path: window.location.pathname,
              page_type: determinePageType(),
            },
            interaction: {
              link_text: tag?.name ?? 'missing tag name',
              link_url:
                tag?.isLink && tag?.path
                  ? pdpPath || tag?.path
                  : 'missing link url',
            },
          });
          clickHandler(arg);
        }
      : undefined;

  if (tag?.isLink && tag?.name && tag?.path) {
    return (
      <a
        href={pdpPath || tag?.path}
        className={styles.link}
        ref={(el) => {
          ref.current = el;
        }}
        onClick={() => {
          trackTagEvent({
            eventName: TagEventName.selectTag,
            page: {
              path: window.location.pathname,
              page_type: determinePageType(),
            },
            interaction: {
              link_text: tag?.name ?? 'missing tag name',
              link_url:
                tag?.isLink && tag?.path
                  ? pdpPath || tag?.path
                  : 'missing link url',
            },
          });
        }}
      >
        {tag?.name}
      </a>
    );
  } else if (tag?.name && tag?.path && clickWrapper) {
    return (
      <button
        className={styles.button}
        onClick={() => clickWrapper({ path: pdpPath || tag?.path })}
        aria-label={tag?.name}
        ref={(el) => {
          ref.current = el;
        }}
      >
        {tag?.name}
      </button>
    );
  } else
    return (
      <span
        className={styles.text}
        ref={(el) => {
          ref.current = el;
        }}
      >
        {tag?.name}
      </span>
    );
};

const TagsContainer = ({
  hitId,
  tags = [],
  pdpPath,
  activeVariantSku,
  clickHandler,
  locale,
}: TagsContainerProps) => {
  // still a work in progress so no offence taken if anything gets changed
  // concept is to use the hit.tags to lookup the tags from the product-tags.json
  // hit.tags has colours but not variants 🙃
  // tags from product-tags.json has variants but not colours 🙃

  const { selectedVariant, selectedColourId } = usePdp();

  if (!tags || tags.length === 0 || !FEATURE_FLAG_PRODUCT_TAGS) {
    return null;
  }

  const getTagColour = (tag: ProductTag) => {
    if (tag?.colourOverride) {
      return tag?.colourOverride;
    }
    if (tag?.hierarchy === 'primary' && tag?.isLink) {
      return TagColour.black;
    }
    if (tag?.hierarchy === 'secondary' && tag?.isLink) {
      return TagColour.grey;
    }
    if (tag?.hierarchy === 'primary' && !tag?.isLink) {
      return TagColour.red;
    }
    if (tag?.hierarchy === 'secondary' && !tag?.isLink) {
      return TagColour.redOutline;
    }
  };

  const allTags = tagsFromFile(locale ?? 'en');
  const renderedTags = tags.map((tag) => {
    const tagInfo = tag?.id ? allTags[tag.id] : null;

    if (tagInfo?.start_date && tagInfo?.end_date) {
      const validDates = isWithinDateRangePST(
        tagInfo?.start_date,
        tagInfo?.end_date
      );

      if (!validDates) {
        return null;
      }
    }

    const hasVariants = tagInfo?.variants && tagInfo.variants.length !== 0;
    const selectedSku = selectedVariant?.sku ?? null;
    const activeVariantSkuNumber = activeVariantSku ?? null;
    const hitColours = tag?.colours ?? [];
    const hasColours = hitColours && hitColours.length !== 0;

    const scenarioOne =
      hasColours && selectedColourId && hitColours?.includes(selectedColourId);
    const scenarioTwo =
      hasVariants && selectedSku && tagInfo?.variants.includes(selectedSku);
    const scenarioThree =
      hasVariants &&
      activeVariantSkuNumber &&
      tagInfo?.variants.includes(activeVariantSkuNumber);
    const scenarioFour = !hasVariants && !hasColours;
    const TagComponent = (
      <Tag
        component={
          <RenderTagComponent
            tag={tag}
            clickHandler={clickHandler}
            pdpPath={pdpPath}
          />
        }
        colourOverride={getTagColour(tag)}
        key={`tag-${hitId}-${tag?.name}`}
      />
    );

    if (scenarioOne || scenarioTwo || scenarioThree || scenarioFour) {
      return TagComponent;
    }

    return null;
  });

  return <>{renderedTags}</>;
};

export default TagsContainer;
